import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { JOIN_PATH, type JoinerState, type JoinerView, type MyJoinRequest } from '@scoutaround/script-breakdown-shared';

import { api, ApiError, getToken, isWorkspaceRoleRequired } from '../lib/api.js';
import { forgetJoinToken, heldJoinToken, markJoinIntent, takeJoinIntent } from '../lib/join-link.js';
import { CLIENT_SITE_URL } from '../lib/config.js';
import { CONTACT_URL } from '../components/WorkspaceAccessRequired.js';
import { enterHref } from './EnterPage.js';
import '../styles/join.css';

/**
 * AN INVITATION LINK, OPENED (owner brief "Shareable Project Invitation Links &
 * Approval Workflow", 2026-11-02, §3).
 *
 * The link is `/join#<token>`; by the time this renders `main.tsx` has moved the
 * token into the tab (`lib/join-link.ts`) and the address reads `/join`. The page
 * then shows ONE state at a time, each with at most one primary action:
 *
 * - signed out — the production's title and "Request to join", which signs in
 *   (or up) on ScoutAround first and comes back here to ask, once;
 * - signed in — where this person stands, from the server: may ask, request sent,
 *   approved and waiting for the account to be enabled, approved (enter), on the
 *   team, not approved, no longer on the team, or the link no longer valid.
 *
 * NOTHING ABOUT THE PRODUCTION BEYOND ITS TITLE, ever — no member, scene or
 * location — and nothing at all for a link that does not work. A link never
 * grants access by itself: every answer here comes from a request the owner
 * decides.
 *
 * With no link in the tab (a fresh tab from an email, or `/join` typed) the page
 * lists the person's own requests instead.
 *
 * Works for an account WITHOUT the Production workspace: the website hands such
 * an account over for this page only, and the join endpoints admit it (the
 * account is told its approval waits for ScoutAround to enable it).
 */
export function JoinPage() {
  const [token] = useState(heldJoinToken);
  const [signedIn, setSignedIn] = useState(() => getToken() !== null);
  // Stable, so the pages below can name it as an effect dependency.
  const signedOut = useCallback(() => {
    setSignedIn(false);
  }, []);

  return (
    <main className="join-page" data-testid="join-page">
      <div className="card1 join-card">
        <p className="kick">Production workspace</p>
        {token === null ? (
          <MyRequests signedIn={signedIn} onSignedOut={signedOut} />
        ) : signedIn ? (
          <SignedInLink token={token} onSignedOut={signedOut} />
        ) : (
          <SignedOutLink token={token} />
        )}
      </div>
    </main>
  );
}

function signIn(): void {
  window.location.assign(enterHref(JOIN_PATH));
}

/** The title for a usable link — or the one sentence for any other. */
function SignedOutLink({ token }: { token: string }) {
  const [title, setTitle] = useState<string | null>(null);
  const [failure, setFailure] = useState<'invalid' | 'unavailable' | null>(null);

  useEffect(() => {
    let live = true;
    api
      .previewJoinLink(token)
      .then((preview) => {
        if (live) setTitle(preview.projectTitle);
      })
      .catch((error: unknown) => {
        if (!live) return;
        setFailure(error instanceof ApiError && error.status === 404 ? 'invalid' : 'unavailable');
      });
    return () => {
      live = false;
    };
  }, [token]);

  if (failure === 'invalid') return <NoLongerValid />;
  if (failure === 'unavailable') {
    return (
      <Unavailable
        onRetry={() => {
          window.location.reload();
        }}
      />
    );
  }
  if (title === null) return <Checking />;

  return (
    <JoinInvite
      title={title}
      action={
        <button
          type="button"
          className="btn btn-primary"
          data-testid="join-request"
          onClick={() => {
            // Asked once, on the way back from signing in — not a second click.
            markJoinIntent();
            signIn();
          }}
        >
          Request to join
        </button>
      }
      note={
        <>
          You will sign in to ScoutAround first — or create a free account — and come straight back here. The
          production’s owner approves every request before anyone gets access.
        </>
      }
    />
  );
}

function SignedInLink({ token, onSignedOut }: { token: string; onSignedOut: () => void }) {
  const [view, setView] = useState<JoinerView | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  // StrictMode runs effects twice in development; the request must still be asked once.
  const asked = useRef(false);

  const ask = () => {
    setBusy(true);
    setActionError(null);
    api
      .requestToJoin(token)
      .then((answer) => {
        setView(answer);
      })
      .catch((error: unknown) => {
        if (error instanceof ApiError && error.status === 401) {
          onSignedOut();
          return;
        }
        if (error instanceof ApiError && error.status === 404) {
          setView({ state: 'INVALID', projectTitle: null, projectId: null, requestId: null, requestedAt: null, decidedAt: null });
          return;
        }
        setActionError(error instanceof ApiError ? error.message : 'Your request did not go through. Try again.');
      })
      .finally(() => {
        setBusy(false);
      });
  };

  useEffect(() => {
    let live = true;
    setFailure(null);
    api
      .joinStatus(token)
      .then((answer) => {
        if (!live) return;
        setView(answer);
        // Pressed "Request to join" before signing in: ask now, once.
        if (answer.state === 'CAN_REQUEST' && !asked.current && takeJoinIntent()) {
          asked.current = true;
          ask();
        }
      })
      .catch((error: unknown) => {
        if (!live) return;
        if (error instanceof ApiError && error.status === 401) {
          onSignedOut();
          return;
        }
        setFailure(error instanceof ApiError ? error.message : 'We could not check that link just now.');
      });
    return () => {
      live = false;
    };
    // `ask` and `onSignedOut` are stable enough for a page that lives once per link.
  }, [token, attempt]);

  if (failure !== null) {
    return (
      <Unavailable
        message={failure}
        onRetry={() => {
          setAttempt((n) => n + 1);
        }}
      />
    );
  }
  if (view === null) return <Checking />;

  return (
    <StateView
      view={view}
      busy={busy}
      actionError={actionError}
      onRequest={ask}
      onActivationNeeded={() => {
        setView({ ...view, state: 'AWAITING_ACTIVATION' });
      }}
    />
  );
}

/** One state, its sentence, and at most one primary action. */
function StateView({
  view,
  busy,
  actionError,
  onRequest,
  onActivationNeeded,
}: {
  view: JoinerView;
  busy: boolean;
  actionError: string | null;
  onRequest: () => void;
  onActivationNeeded: () => void;
}) {
  const title = view.projectTitle ?? 'this production';

  switch (view.state) {
    case 'CAN_REQUEST':
      return (
        <JoinInvite
          title={title}
          action={
            <button type="button" className="btn btn-primary" data-testid="join-request" disabled={busy} onClick={onRequest}>
              {busy ? 'Sending request…' : 'Request to join'}
            </button>
          }
          note="The production’s owner approves every request before anyone gets access."
          error={actionError}
        />
      );
    case 'PENDING_APPROVAL':
      return (
        <State testId="join-state-pending" heading="Request sent">
          <p className="join-lede" data-testid="join-lede">
            Your request to join this production is waiting for approval.
          </p>
          <p className="join-note">
            We will email you when the owner of <strong>{title}</strong> decides. You can close this page; nothing
            on the production opens until then.
          </p>
        </State>
      );
    case 'AWAITING_ACTIVATION':
      return (
        <State testId="join-state-awaiting-activation" heading="Approved — one step left">
          <p className="join-lede" data-testid="join-lede">
            The owner of <strong>{title}</strong> approved your request.
          </p>
          <p className="join-note">
            ScoutAround has not enabled the Production workspace for your account yet, so the production is not open
            to you. Ask us to enable it; once it is, open this page again and enter the production.
          </p>
          <div className="join-actions">
            <a className="btn btn-primary" href={CONTACT_URL} data-testid="join-contact">
              Ask ScoutAround to enable it
            </a>
          </div>
        </State>
      );
    case 'READY_TO_ENTER':
    case 'MEMBER':
      return (
        <State
          testId={view.state === 'MEMBER' ? 'join-state-member' : 'join-state-approved'}
          heading={view.state === 'MEMBER' ? 'You are on the team' : 'Your request was approved'}
        >
          <p className="join-lede" data-testid="join-lede">
            {view.state === 'MEMBER' ? (
              <>
                You are a member of <strong>{title}</strong>.
              </>
            ) : (
              <>
                The owner of <strong>{title}</strong> approved your request. Enter the production to join the team.
              </>
            )}
          </p>
          <EnterButton
            requestId={view.state === 'READY_TO_ENTER' ? view.requestId : null}
            projectId={view.projectId}
            onActivationNeeded={onActivationNeeded}
          />
        </State>
      );
    case 'REJECTED':
      return (
        <State testId="join-state-rejected" heading="Your request was not approved">
          <p className="join-lede" data-testid="join-lede">
            The owner of <strong>{title}</strong> did not approve this request. If you think that is a mistake, ask the
            person who shared the link with you.
          </p>
        </State>
      );
    case 'ENDED':
      return (
        <State testId="join-state-ended" heading="You are no longer on this production">
          <p className="join-lede" data-testid="join-lede">
            Your place on <strong>{title}</strong> has ended. Ask its team for a new invitation if you should be back.
          </p>
        </State>
      );
    case 'INVALID':
      return <NoLongerValid />;
  }
}

/**
 * "Enter production". A request whose approval waited for the workspace becomes a
 * membership here (the server checks the role); otherwise this only goes to the
 * production. A full load, like every sign-in boundary: membership just changed.
 */
function EnterButton({
  requestId,
  projectId,
  onActivationNeeded,
}: {
  requestId: string | null;
  projectId: string | null;
  onActivationNeeded: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const go = (id: string) => {
    forgetJoinToken();
    window.location.replace(`/projects/${id}/overview`);
  };

  return (
    <div className="join-actions">
      <button
        type="button"
        className="btn btn-primary"
        data-testid="join-enter"
        disabled={busy}
        onClick={() => {
          if (requestId === null) {
            if (projectId !== null) go(projectId);
            return;
          }
          setBusy(true);
          setError(null);
          api
            .enterJoinedProduction(requestId)
            .then((result) => {
              go(result.projectId);
            })
            .catch((e: unknown) => {
              setBusy(false);
              if (isWorkspaceRoleRequired(e)) {
                onActivationNeeded();
                return;
              }
              setError(e instanceof ApiError ? e.message : 'We could not open the production just now. Try again.');
            });
        }}
      >
        {busy ? 'Opening…' : 'Enter production'}
      </button>
      {error === null ? null : (
        <p className="join-error" role="alert" data-testid="join-enter-error">
          {error}
        </p>
      )}
    </div>
  );
}

/** With no link in this tab: the person's own requests, or a way to sign in to see them. */
function MyRequests({ signedIn, onSignedOut }: { signedIn: boolean; onSignedOut: () => void }) {
  const [requests, setRequests] = useState<MyJoinRequest[] | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!signedIn) return;
    let live = true;
    setFailure(null);
    api
      .myJoinRequests()
      .then((answer) => {
        if (live) setRequests(answer.requests);
      })
      .catch((error: unknown) => {
        if (!live) return;
        if (error instanceof ApiError && error.status === 401) {
          onSignedOut();
          return;
        }
        setFailure(error instanceof ApiError ? error.message : 'Your requests could not be loaded just now.');
      });
    return () => {
      live = false;
    };
  }, [signedIn, attempt, onSignedOut]);

  if (!signedIn) {
    return (
      <State testId="join-no-link" heading="Your requests to join productions">
        <p className="join-lede" data-testid="join-lede">
          Open the invitation link you were sent to ask to join a production. To see the requests you have already
          made, sign in.
        </p>
        <div className="join-actions">
          <button type="button" className="btn btn-primary" data-testid="join-signin" onClick={signIn}>
            Sign in
          </button>
        </div>
      </State>
    );
  }

  if (failure !== null) {
    return (
      <Unavailable
        message={failure}
        onRetry={() => {
          setAttempt((n) => n + 1);
        }}
      />
    );
  }
  if (requests === null) return <Checking />;

  return (
    <State testId="join-mine" heading="Your requests to join productions">
      {requests.length === 0 ? (
        <p className="join-lede" data-testid="join-mine-empty">
          You have not asked to join a production. To ask, open the invitation link you were sent.
        </p>
      ) : (
        <ul className="join-mine-list" data-testid="join-mine-list">
          {requests.map((r) => (
            <MyRequestRow key={r.requestId} request={r} />
          ))}
        </ul>
      )}
    </State>
  );
}

const MINE_LABELS: Readonly<Record<JoinerState, string>> = {
  CAN_REQUEST: 'Not asked yet',
  PENDING_APPROVAL: 'Waiting for approval',
  AWAITING_ACTIVATION: 'Approved — waiting for ScoutAround to enable your account',
  READY_TO_ENTER: 'Approved',
  MEMBER: 'On the team',
  REJECTED: 'Not approved',
  ENDED: 'No longer on the team',
  INVALID: 'No longer valid',
};

function MyRequestRow({ request }: { request: MyJoinRequest }) {
  const [state, setState] = useState(request.state);

  return (
    <li className="join-mine-row" data-testid={`join-mine-row-${request.requestId}`}>
      <div className="join-mine-main">
        <span className="join-mine-title">{request.projectTitle}</span>
        <span className="join-mine-state" data-state={state} data-testid={`join-mine-state-${request.requestId}`}>
          {MINE_LABELS[state]}
        </span>
      </div>
      {state === 'READY_TO_ENTER' || state === 'MEMBER' ? (
        <EnterButton
          requestId={state === 'READY_TO_ENTER' ? request.requestId : null}
          projectId={request.projectId}
          onActivationNeeded={() => {
            setState('AWAITING_ACTIVATION');
          }}
        />
      ) : state === 'AWAITING_ACTIVATION' ? (
        <a className="join-link" href={CONTACT_URL} data-testid={`join-mine-contact-${request.requestId}`}>
          Ask ScoutAround to enable it
        </a>
      ) : null}
    </li>
  );
}

// ─── Small pieces ─────────────────────────────────────────────────────────────

function JoinInvite({
  title,
  action,
  note,
  error = null,
}: {
  title: string;
  action: ReactNode;
  note: ReactNode;
  error?: string | null;
}) {
  return (
    <State testId="join-invite" heading={`Join ${title}`}>
      <p className="join-lede" data-testid="join-lede">
        You have been invited to request access to this production on ScoutAround.
      </p>
      <div className="join-actions">{action}</div>
      {error === null ? null : (
        <p className="join-error" role="alert" data-testid="join-request-error">
          {error}
        </p>
      )}
      <p className="join-note" data-testid="join-note">
        {note}
      </p>
    </State>
  );
}

function State({ testId, heading, children }: { testId: string; heading: string; children: ReactNode }) {
  return (
    <section data-testid={testId} aria-live="polite">
      <h1 className="dh join-title" data-testid="join-title">
        {heading}
      </h1>
      {children}
    </section>
  );
}

function NoLongerValid() {
  return (
    <State testId="join-invalid" heading="This invitation link is no longer valid">
      <p className="join-lede" data-testid="join-lede">
        Ask the person who shared it with you for a new link.
      </p>
      <div className="join-actions">
        <a className="btn btn-ghost" href={`${CLIENT_SITE_URL}/`} data-testid="join-home">
          Back to ScoutAround
        </a>
      </div>
    </State>
  );
}

function Checking() {
  return (
    <p className="dh join-title" data-testid="join-loading">
      Checking that link…
    </p>
  );
}

function Unavailable({ message, onRetry }: { message?: string; onRetry: () => void }) {
  return (
    <State testId="join-unavailable" heading="We could not check that link">
      <p className="join-lede" data-testid="join-lede">
        {message ?? 'ScoutAround did not answer just now.'} Nothing has changed — try again in a moment.
      </p>
      <div className="join-actions">
        <button type="button" className="btn btn-primary" data-testid="join-retry" onClick={onRetry}>
          Try again
        </button>
      </div>
    </State>
  );
}
